import I18n from 'i18n'
import type { Locale as DateFnsLocale } from 'date-fns'
import { nl as dateFnsNl, enIE as dateFnsEn, fi as dateFnsFi } from 'date-fns/locale'
import type { LocaleData as TimeAgoLocale } from 'javascript-time-ago'
import timeAgoNl from 'javascript-time-ago/locale/nl'
import timeAgoEn from 'javascript-time-ago/locale/en'
import timeAgoFi from 'javascript-time-ago/locale/fi'

// The locales the platform interface is available in. To add a language:
// 1. add its translation files (config/locales/**/<code>.yml),
// 2. add it to config.i18n.available_locales (config/application.rb),
// 3. add an entry below.
// Everything else (language selectors, date pickers, relative times, fallbacks)
// is derived from this list. moment bundles all its locales, so it needs no entry.
interface LocaleDefinition {
  // Name of the language in the language itself, as shown in language selectors.
  name: string
  // Locale used by date-fns (react-datepicker). English uses the Irish locale,
  // since that one matches the rest of Europe (weeks start on Mondays).
  dateFns: DateFnsLocale
  // Locale used by javascript-time-ago ("3 minutes ago").
  timeAgo: TimeAgoLocale
}

const localeDefinitions = {
  nl: { name: 'Nederlands', dateFns: dateFnsNl, timeAgo: timeAgoNl },
  en: { name: 'English', dateFns: dateFnsEn, timeAgo: timeAgoEn },
  fi: { name: 'Suomi', dateFns: dateFnsFi, timeAgo: timeAgoFi }
}

export type SupportedLocale = keyof typeof localeDefinitions

export const LOCALES: Readonly<Record<SupportedLocale, LocaleDefinition>> = Object.freeze(localeDefinitions)

// Missing translations fall back to this locale (except for nl, which has no fallback).
export const FALLBACK_LOCALE: SupportedLocale = 'en'

export const supportedLocales = Object.keys(LOCALES) as SupportedLocale[]

export const isSupportedLocale = (locale: unknown): locale is SupportedLocale =>
  typeof locale === 'string' && Object.prototype.hasOwnProperty.call(LOCALES, locale)

// Maps a language tag such as 'fi-FI' or 'en-US' to a supported locale, or undefined.
export const toSupportedLocale = (tag?: string | null): SupportedLocale | undefined => {
  if (!tag) return undefined
  const language = tag.toLowerCase().split(/[-_]/)[0]
  return isSupportedLocale(language) ? language : undefined
}

export const currentLocale = (): SupportedLocale => toSupportedLocale(I18n.locale) ?? FALLBACK_LOCALE

export const localeDefinition = (locale: string = I18n.locale): LocaleDefinition =>
  LOCALES[toSupportedLocale(locale) ?? FALLBACK_LOCALE]

// Picks the value for the current locale from a per-locale object, e.g. { nl: '...', en: '...' },
// falling back to English and then to any available value.
export const localized = <T>(values: Partial<Record<string, T>> | null | undefined, locale: string = I18n.locale): T | undefined => {
  if (!values) return undefined
  return values[locale] ?? values[FALLBACK_LOCALE] ?? Object.values(values).find(value => value !== undefined && value !== null)
}
